import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom';
import { callApi } from '../../api/callApi';
import { API_ENDPOINT, API_URL } from '../../api/constant';
import { useSelector } from 'react-redux';
import { IRole, IUser } from '../../interfaces/user';
import Layout from '../../components/Layout/Layout';
import Table from '../../components/UI/CommonTable/CommonTable';
import Pagination from '../../components/UI/List/Pagination';
import moment from '../../utility/moment-timezone';
import { showToastNotification } from '../../utility/helper';

const Users: React.FC = () => {
    const roles: IRole[] = useSelector((state: any) => state.root.roles)
    const [users, setUsers] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    let roleId = roles.find((role: IRole) => role.name.toLowerCase().includes('user'))?.id ?? ''
    const navigate = useNavigate()

    useEffect(() => {
        getProducts(currentPage);
    }, [currentPage]);
    const getProducts = async (page: number = 1) => {
        try {
            setLoading(true);

            const response = await callApi(API_URL + API_ENDPOINT.auth + `user/list?page=${page}&sortType=DESC&roleId=${roleId}`, { method: 'GET' });
            const { currentPage, data, totalPages } = response;
            setUsers(data);
            setCurrentPage(currentPage);
            setTotalPages(totalPages);
        } catch (error: any) {
            console.error('Failed to fetch products:', error.message);
        } finally {
            setLoading(false);
        }
    };


    const verifyUser = async (userId: string) => {
        try {
            setLoading(true);
            ///auth/api/user/update/{userId}
            await callApi(API_URL + API_ENDPOINT.auth + `user/update/${userId}`, { method: 'PUT', data: { isVerified: true } });
            showToastNotification('success', 'User verified successfully');
            getProducts(currentPage);
        } catch (error: any) {
            console.error('Failed to verify user:', error.message);
            showToastNotification('danger', 'Failed to verify user');
        } finally {
            setLoading(false);
        }
    }

    const sendVerificationEmail = async (user: IUser) => {
        try {
            setLoading(true);
            ////auth/api/user/send/verification
            await callApi(API_URL + API_ENDPOINT.auth + `user/send/verification`, { method: 'POST', data: { email: user.email } });
            showToastNotification('success', 'Verification email sent successfully');
        } catch (error: any) {
            console.error('Failed to send verification email:', error.message);
            showToastNotification('danger', 'Failed to send verification email');
        } finally {
            setLoading(false);
        }
    }

    const columns = [
        { header: "Full Name", accessor: "fullName" },
        { header: "Email", accessor: "email" },
        { header: "Phone", accessor: "phone" },
        {
            header: "Verified",
            accessor: "isVerified",
            render: (isVerified: boolean) => (isVerified ? "Yes" : "No"),
        },
        { header: "Joined At", accessor: "createdAt", render: (createdAt: string) => moment(createdAt).format("DD MMM, YYYY hh:mm a") },
    ];

    const actions = [
        {
            icon: "bi bi-eye",
            color: "success",
            onClick: (user: IUser) => navigate(`/user-details/${user.id}`, { state: { user: user } }),
            tooltip: 'View User Details'
        },
        {
            icon: "bi bi-person-fill-check",
            color: "danger",
            onClick: (user: IUser) => verifyUser(user.id),
            show: (user: IUser) => !user.isVerified,
            tooltip: 'Verify User'
        },
        {
            icon: 'bi bi-envelope-arrow-up-fill',
            color: 'info',
            onClick: (user: IUser) => sendVerificationEmail(user),
            show: (user: IUser) => !user.isVerified,
            tooltip: 'Send Verification Email'
        }

    ];

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };
    const downloadReport = async () => {
        try {
            const response = await callApi(API_URL + API_ENDPOINT.auth + 'user/export-excel?roleId=' + roleId, {
                method: 'GET',
                responseType: 'blob'
            });

            if (!response) {
                throw new Error('Failed to generate report');
            }

            const blob = new Blob([response], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(blob);

            const a = document.createElement('a');
            a.href = url;
            a.download = `User_Report_${Date.now()}.xlsx`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading the report:', error);
        }
    }
    return (
        <Layout>
            <div className='d-flex justify-content-between align-items-start'>
                <h1 className="app-page-title">Users</h1>
                <button className='btn app-btn-primary d-flex align-items-center gap-1' onClick={() => downloadReport()} >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-download" viewBox="0 0 16 16">
                        <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5" />
                        <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708z" />
                    </svg>
                    Download Report
                </button>
            </div>
            <div className="app-card app-card-orders-table shadow-sm mb-4 p-4">
                <div className="app-card-body">
                    <Table
                        columns={columns}
                        data={users}
                        loading={loading}
                        actions={actions}
                        emptyMessage="No products available."
                    />
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                    />
                </div>
            </div>
        </Layout>
    )
}

export default Users